<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import {
    MenuUnfoldOutlined,
    UserOutlined,
    LogoutOutlined,
    LockOutlined
} from '@ant-design/icons-vue';
import { logout } from '@/api/auth/logout';
import { clearDynamicRoutes } from '@/router/injectPrivateRoutesByPermission';
import ChangePasswordModal from '@/components/layouts/ChangePasswordModal.vue';
import { useAdminInfo } from '@/stores/useAdminInfo';
import { goToGrowthSystemHome, resolveGrowthSystemHomeUrl } from '@/utils/growthSystemNav';

const props = defineProps({
    collapsed: {
        type: Boolean,
        required: true
    },
    isMobile: {
        type: Boolean,
        required: true
    },
    username: {
        type: String,
        required: true
    }
});

const emit = defineEmits(['update:collapsed']);

const router = useRouter();
const adminStore = useAdminInfo();
const showChangePassword = ref(false);

const growthHomeUrl = resolveGrowthSystemHomeUrl(
    import.meta.env.VITE_GROWTH_SYSTEM_URL
);

const goGrowthSystem = () => {
    goToGrowthSystemHome(import.meta.env.VITE_GROWTH_SYSTEM_URL);
};

const goToWelcome = () => {
    router.push('/welcome');
};

const handleLogout = async () => {
    await logout();
    localStorage.removeItem('accessToken');
    adminStore.clearAdminInfo();
    clearDynamicRoutes(); // 清除動態路由
    router.push('/login');
};

const toggleCollapsed = () => {
    emit('update:collapsed', !props.collapsed);
};
</script>

<template>
    <a-layout-header
        class="admin-top-header no-print !px-0 flex h-16 min-h-16 justify-between items-center fixed z-[60] border-b border-slate-200/90 bg-white/95 shadow-sm backdrop-blur-md [box-shadow:inset_0_-2px_0_0_rgba(102,209,171,0.28)]"
        :style="{
            width: isMobile
                ? '100%'
                : `calc(100% - ${collapsed ? '80px' : '200px'})`,
            left: isMobile ? '0' : collapsed ? '80px' : '200px',
            transition: 'all 0.3s'
        }"
    >
        <div class="flex items-center">
            <div
                v-if="!isMobile || (isMobile && collapsed)"
                class="ml-3 flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-lg text-lg text-[#565962] transition-colors hover:bg-[rgba(196,163,90,0.12)] hover:text-[#8b6914]"
                @click="toggleCollapsed"
            >
                <menu-unfold-outlined
                    class="transition-transform duration-300"
                    :style="{
                        transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)'
                    }"
                />
            </div>
            <button
                v-if="growthHomeUrl"
                type="button"
                class="ml-2 inline-flex items-center rounded-lg border border-slate-200/90 bg-slate-50/90 px-2.5 py-1.5 text-xs font-semibold text-[#8b6914] transition-colors hover:border-[rgba(196,163,90,0.45)] hover:bg-[rgba(196,163,90,0.1)] sm:text-sm"
                @click="goGrowthSystem"
            >
                ‹ 世界樹文明
            </button>
        </div>

        <div class="flex items-center">
            <div
                v-if="isMobile"
                class="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2 cursor-pointer"
                @click="goToWelcome"
            >
                <img
                    src="@/assets/logo.png"
                    alt="Logo"
                    class="w-6 h-6 object-contain"
                />
                <span class="text-lg font-semibold tracking-tight text-[#272b36]">管理員後台</span>
            </div>

            <a-dropdown
                class="mr-3 sm:mr-5"
                :trigger="isMobile ? ['click'] : ['hover']"
            >
                <div
                    class="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200/80 bg-slate-50/80 px-3 py-1.5 transition-colors hover:border-[rgba(196,163,90,0.45)] hover:bg-[rgba(102,209,171,0.08)]"
                >
                    <span
                        v-if="!isMobile"
                        class="max-w-[220px] truncate text-sm font-medium text-[#272b36]"
                    >
                        {{ username }}
                    </span>
                    <user-outlined
                        class="text-[#8b6914]"
                        :class="isMobile ? 'mr-[-24px]' : ''"
                    />
                </div>
                <template #overlay>
                    <a-menu>
                        <a-menu-item v-if="isMobile" disabled>
                            <div class="flex items-center gap-2">
                                <span class="text-slate-800 font-medium truncate max-w-[220px]">
                                    {{ username }}
                                </span>
                            </div>
                        </a-menu-item>
                        <a-menu-divider v-if="isMobile" />
                        <a-menu-item
                            v-if="!adminStore.isConsultantOnly"
                            @click="showChangePassword = true"
                        >
                            <div class="flex items-center gap-2">
                                <LockOutlined />
                                <span>修改密碼</span>
                            </div>
                        </a-menu-item>
                        <a-menu-item @click="handleLogout">
                            <div class="flex items-center gap-2">
                                <logout-outlined />
                                <span>登出</span>
                            </div>
                        </a-menu-item>
                    </a-menu>
                </template>
            </a-dropdown>
            <ChangePasswordModal
                v-model:open="showChangePassword"
                @success="showChangePassword = false"
            />
        </div>
    </a-layout-header>
</template>
